import { useAuth } from "@/features/auth/hooks/useAuth";
import type { Module, RoleName } from "@/config/roles";

/**
 * usePermissions
 *
 * Centralised permission hook. Replaces the scattered
 * `OFFICER_TYPES.includes(userData?.userType)` checks across the codebase.
 *
 * Strategy A (backward-compatible opt-in restriction):
 *   ADMIN                   → isFullAccess (bypass all role checks)
 *   OFFICER + no roles      → isFullAccess (existing officers unaffected)
 *   OFFICER + roles present → only the assigned roles grant access
 *   USER / EMPLOYEE         → canRead always true (navigation always visible);
 *                             canWrite always false (no officer-only actions)
 *
 * Role gating via requireModule is an OFFICER-only concept.
 * Non-officers always see all navigation items; what data they can
 * see is enforced at the API/backend level (e.g. owner_id filter).
 *
 * canRead('announcements')  → true if user can view/list that module
 * canWrite('announcements') → true if user can create/update/delete/export
 *
 * write implies read for the same module.
 */
export function usePermissions() {
  const { userData, roles } = useAuth();
  const userType = (userData?.userType ?? "").toLowerCase();

  const isAdmin    = userType === "admin";
  const isOfficer  = userType === "officer" || isAdmin;
  const isEmployee = !isOfficer;

  // Full access: admin OR officer with no role restrictions assigned yet
  const isFullAccess = isAdmin || (isOfficer && roles.length === 0);

  const canRead = (module: Module): boolean => {
    if (isFullAccess) return true;
    // Employees always have navigation access; data is filtered by the API.
    if (isEmployee)   return true;
    // Restricted officer: must hold _read OR _write for this module
    return (
      roles.includes(`${module}_read`  as RoleName) ||
      roles.includes(`${module}_write` as RoleName)
    );
  };

  const canWrite = (module: Module): boolean => {
    if (isFullAccess) return true;
    if (isEmployee)   return false;
    // Restricted officer: must hold _write
    return roles.includes(`${module}_write` as RoleName);
  };

  // SOUSER: special handling for announcements module
  const isSouser = userType === "souser";
  const souserCanRead = (module: Module): boolean => {
    if (module !== "announcements") return false;
    return userData?.announcements_read || userData?.announcements_write || false;
  };

  const souserCanWrite = (module: Module): boolean => {
    if (module !== "announcements") return false;
    return userData?.announcements_write || false;
  };

  return {
    isAdmin,
    isOfficer,
    isEmployee,
    isFullAccess,
    canRead: isSouser ? souserCanRead : canRead,
    canWrite: isSouser ? souserCanWrite : canWrite,
    roles,
  };
}
